import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { BehaviorSubject, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

const TOKEN_KEY = 'JWT_TOKEN';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Mutex compartido entre todas las peticiones: cuando el access token expira y
// llegan N requests con 401 simultáneos, solo se dispara UN refresh y el resto
// espera al BehaviorSubject para reintentar con el nuevo token.
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

const isAuthEndpoint = (url: string): boolean =>
	url.includes('/auth/login') ||
	url.includes('/auth/register') ||
	url.includes('/auth/logout') ||
	url.includes('/auth/refresh') ||
	url.includes('/auth/reset-password') ||
	url.includes('/auth/update-password');

const addAuthHeader = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> =>
	request.clone({
		withCredentials: false,
		setHeaders: {
			Authorization: `Bearer ${token}`,
			Accept: 'application/json',
			...(!(request.body instanceof FormData) && {
				'Content-Type': 'application/json',
			}),
		},
	});

export const AuthInterceptor: HttpInterceptorFn = (
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const router = inject(Router);
	const authService = inject(AuthService);

	if (isAuthEndpoint(request.url)) {
		return next(request);
	}

	const token = localStorage.getItem(TOKEN_KEY);

	if (!token) {
		console.warn('⚠️ No se encontró token - Petición sin autorización');
		return next(request);
	}

	if (!token.includes('.')) {
		console.error('❌ Error: Token inválido - No es un JWT válido');
		localStorage.removeItem(TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		router.navigate(['/login']);
		return throwError(() => new Error('Token inválido'));
	}

	return next(addAuthHeader(request, token)).pipe(
		catchError((error: HttpErrorResponse) => {
			if (error.status !== 401) {
				return throwError(() => error);
			}

			const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
			if (!refreshToken) {
				localStorage.removeItem(TOKEN_KEY);
				router.navigate(['/login']);
				return throwError(() => error);
			}

			return handle401(request, next, authService, router);
		}),
	);
};

function handle401(
	request: HttpRequest<unknown>,
	next: HttpHandlerFn,
	authService: AuthService,
	router: Router,
): Observable<HttpEvent<unknown>> {
	if (isRefreshing) {
		return refreshTokenSubject.pipe(
			filter((t): t is string => t !== null),
			take(1),
			switchMap((newToken) => next(addAuthHeader(request, newToken))),
		);
	}

	isRefreshing = true;
	refreshTokenSubject.next(null);

	return authService.refreshToken().pipe(
		switchMap((response: { access_token: string }) => {
			isRefreshing = false;
			refreshTokenSubject.next(response.access_token);
			return next(addAuthHeader(request, response.access_token));
		}),
		catchError((refreshError) => {
			isRefreshing = false;
			refreshTokenSubject.next(null);
			localStorage.removeItem(TOKEN_KEY);
			localStorage.removeItem(REFRESH_TOKEN_KEY);
			router.navigate(['/login']);
			return throwError(() => refreshError);
		}),
	);
}
